import {
  Badge,
  Button,
  Card,
  DataTable,
  DatePicker,
  EmptyState,
  FormField,
  Icon,
  Input,
  ListToolbar,
  Text,
  Textarea,
  todayIso,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageActions } from '../../../app/PageActions';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage, formErrors } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useSession } from '../../identity/session';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { useRowMenu, useLoadedForAction } from '../../../app/rowActions';
import {
  useCloseMarketDay,
  useMarketDay,
  useMarketDays,
  useOpenMarketDay,
  type MarketDayListItem,
} from '../api';
import { AssortmentDialog } from '../components/AssortmentDialog';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

/** Market days (MKT-003) – sales records, not a cash register (MKT-010). */
export function MarketDaysPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const days = useMarketDays();
  const columnSort = useColumnSort();
  const [opening, setOpening] = useState(false);
  const write = can('sales.write');
  const navigate = useNavigate();
  const toast = useToast();
  const rowMenu = useRowMenu();
  const closeDay = useCloseMarketDay();
  const [action, setAction] = useState<{
    kind: 'assortment' | 'close';
    day: MarketDayListItem;
  } | null>(null);
  const openDay = () => setOpening(true);

  const columns: DataTableColumn<MarketDayListItem>[] = [
    {
      key: 'name',
      header: t('sales.market.name'),
      primary: true,
      cell: (d) => d.name,
      sortValue: (d) => d.name,
    },
    {
      key: 'date',
      header: t('sales.columns.date'),
      cell: (d) => formatDate(d.held_on),
      sortValue: (d) => d.held_on,
      width: '8rem',
    },
    {
      key: 'place',
      header: t('sales.market.place'),
      cell: (d) => d.place ?? null,
      sortValue: (d) => d.place,
    },
    {
      key: 'sales',
      header: t('sales.market.sales'),
      align: 'end',
      mobile: 'hidden',
      cell: (d) => (
        <Text as="span" numeric>
          {d.sales}
        </Text>
      ),
      sortValue: (d) => d.sales,
    },
    {
      key: 'total',
      header: t('sales.columns.total'),
      align: 'end',
      cell: (d) => (
        <Text as="span" numeric>
          {formatEuro(d.total_cents)}
        </Text>
      ),
      sortValue: (d) => d.total_cents,
    },
    {
      key: 'status',
      header: t('sales.columns.status'),
      mobile: 'subtitle',
      // Only the open day deserves attention; closed is the normal case.
      cell: (d: MarketDayListItem) =>
        d.status === 'open' ? (
          <Badge tone="success" size="sm">
            {t('sales.market.status.open')}
          </Badge>
        ) : null,
      sortValue: (d: MarketDayListItem) =>
        d.status === 'open' ? t('sales.market.status.open') : null,
    },
  ];

  const sortMenu = useSortMenu(columns, days.data ?? [], columnSort);

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={openDay}>
            {t('sales.market.open')}
          </Button>
        </PageActions>
      )}
      <ListToolbar sort={sortMenu} />
      <Card padding="none">
        {days.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(days.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('sales.market.caption')}
            rows={days.data ?? []}
            rowKey={(d) => d.id}
            rowHref={(d) => `/verkauf/markttage/${d.id}`}
            rowActions={rowMenu.table<MarketDayListItem>(
              (d) => `${d.name}, ${formatDate(d.held_on)}`,
              (d) =>
                write && d.status === 'open'
                  ? [
                      {
                        key: 'mode',
                        label: t('sales.market.mode'),
                        onSelect: () => void navigate(`/markt/${d.id}`),
                      },
                      {
                        key: 'assortment',
                        label: t('sales.assortment.edit'),
                        onSelect: () => setAction({ kind: 'assortment', day: d }),
                      },
                      {
                        key: 'close',
                        label: t('sales.market.close'),
                        separated: true,
                        onSelect: () => setAction({ kind: 'close', day: d }),
                      },
                    ]
                  : [],
            )}
            {...columnSort}
            loading={days.isPending}
            columns={columns}
            empty={
              <EmptyState
                title={t('sales.market.emptyTitle')}
                actions={write && <Button onClick={openDay}>{t('sales.market.open')}</Button>}
              />
            }
          />
        )}
      </Card>
      {opening && <OpenMarketDayDialog onClose={() => setOpening(false)} />}
      {action?.kind === 'assortment' && (
        <DayAssortment id={action.day.id} onClose={() => setAction(null)} />
      )}
      <ConfirmDialog
        open={action?.kind === 'close'}
        onClose={() => {
          closeDay.reset();
          setAction(null);
        }}
        tone="default"
        title={t('sales.market.closeTitle', { name: action?.day.name })}
        description={t('sales.market.closeText', {
          count: action?.day.sales ?? 0,
          total: formatEuro(action?.day.total_cents ?? 0),
        })}
        confirmLabel={t('sales.market.close')}
        pending={closeDay.isPending}
        error={closeDay.error ? errorMessage(closeDay.error) : undefined}
        onConfirm={() =>
          action &&
          closeDay.mutate(action.day.id, {
            onSuccess: () => {
              toast.show(t('sales.market.closed'));
              setAction(null);
            },
          })
        }
      />
    </div>
  );
}

function OpenMarketDayDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const open = useOpenMarketDay();
  const [on, setOn] = useState(todayIso());
  const [name, setName] = useState('');
  const [place, setPlace] = useState('');
  const [notes, setNotes] = useState('');
  const errors = formErrors(open.error, {
    name: ['market_day.name'],
    place: ['market_day.place'],
  });
  return (
    <FormDialog
      open
      onClose={onClose}
      title={t('sales.market.openTitle')}
      submitLabel={t('sales.market.open')}
      pending={open.isPending}
      error={errors.general}
      onSubmit={() =>
        open.mutate(
          { held_on: on, name, place: place || null, notes: notes || null },
          {
            onSuccess: (d) => {
              toast.show(t('sales.market.opened', { name: d.name }));
              void navigate(`/verkauf/markttage/${d.id}`);
            },
          },
        )
      }
    >
      <FormField label={t('sales.market.name')} required error={errors.field.name}>
        {(p) => (
          <Input
            {...p}
            value={name}
            placeholder={t('sales.market.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </FormField>
      <div className={styles.row}>
        <FormField label={t('sales.columns.date')} required>
          {(p) => <DatePicker {...p} value={on} onChange={setOn} />}
        </FormField>
        <FormField label={t('sales.market.place')} error={errors.field.place}>
          {(p) => <Input {...p} value={place} onChange={(e) => setPlace(e.target.value)} />}
        </FormField>
      </div>
      <FormField label={t('sales.form.notes')}>
        {(p) => (
          <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}

/** Loads the market day, then shows its assortment dialog. */
function DayAssortment({ id, onClose }: { id: string; onClose: () => void }) {
  const day = useLoadedForAction(useMarketDay(id), onClose);
  return day ? <AssortmentDialog day={day} onClose={onClose} /> : null;
}
